import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../../components/common/HtmlContent';
import useHashScroll from '../../../../hooks/useHashScroll';

/**
 * WarmUpPage Component
 * 
 * Displays all warm-up tools content like the legacy HTML site
 */
const WarmUpPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // Warm Up intro content from the legacy HTML
  const warmUpIntroHtml = `
    <p>
    Whether you are designing on your own, or as part of a group - warming up is a nice method for easing your way and stretching the creatives muscles - plus it can be fun! 
    </p>

    <p>
    There are a lot of different exercises to help stimulate the creative process, and choosing the right option for the right group will go a long way. These are particularly helpful for a design studio session with a multitude of stake holders who might not be designers.
    </p>
  `;

  // Helper function to render tool info table with optional creator row
  const renderToolInfoTable = (category, updated, templates, creator = null) => {
    return (
      <div className="tool-table-wrap">
        <table id="tool-table">
          <tbody>
            <tr>
              <th colSpan="3">Tool Info</th>
            </tr>
            <tr>
              <td>Category</td>
              <td>{category}</td>  
            </tr>
            <tr>
              <td>Updated</td>
              <td>{updated}</td>
            </tr>
            <tr>
              <td>Templates</td>
              <td>{templates}</td>
            </tr>
            {creator && (
              <tr>
                <td>Creator</td>
                <td>{creator}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Templates HTML for each tool
  const lighningDemosTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/design/ideation/warm-up/lightning-demos.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/uXjVM-evNdw=/" className="link-table">Miro</a>
    </>
  );

  const gridTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/design/ideation/warm-up/grid.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/uXjVMC05IMs=/" className="link-table">Miro</a>
    </>
  );

  const alternateUsesTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/design/ideation/warm-up/alternate-uses.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/uXjVM9j5WGs=/" className="link-table">Miro</a>
    </>
  );

  const crazy8sTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/design/ideation/warm-up/crazy-8s.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/uXjVMB6th1g=/" className="link-table">Miro</a>
    </>
  );

  // Creator HTML
  const jakeKnappCreator = <a href="https://www.thesprintbook.com">Jake Knapp</a>;

  return (
    <>
      {/* Warm Up Intro */}
      <div id="section-tools-warm-up" className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/design">Design Tools</Link>&nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
              <Link to="/design/ideation">Ideation</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/design/ideation/warm-up">Warm Up</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={warmUpIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#lightning-demos">Lightning Demos</Link></div>
            <div><Link className="tool-section-cta" to="#grid">Grid</Link></div>
            <div><Link className="tool-section-cta" to="#alternate-uses">Alternate Uses</Link></div>
            <div><Link className="tool-section-cta" to="#crazy-8s">Crazy 8s</Link></div>
          </div>
        </div>
      </div>

      {/* Lightning Demos Section */}
      <main className="case-study">
        <span className="case-tool-title" id="lightning-demos">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="lightning-demos">
            <span className="tool-title-number">
              <Link to="/design/ideation/warm-up/#lightning-demos">Lightning Demos</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/design/ideation">Ideation</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/design/ideation/warm-up">Warm Up</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/design/ideation/warm-up/#lightning-demos"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-lightning-demos" 
            id="tab-lightning-demos-overview" 
            defaultChecked
          />
          <label htmlFor="tab-lightning-demos-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Warm Up", "14/07/23", lighningDemosTemplates, jakeKnappCreator)}
              <div className="tool-opening">
                <p>
                  Considered an excellent method for beginning an ideation session, Lightning Demos lean on the participants to look at how other companies have solved a problem, usually similar but can be anything, inspiring them with their own solutions.
                </p>
                <p>
                  This short research session helps to empathise to the group how important research is to the ideation process. It also removes the requirement for a group to go straight in to solution mode, additionally showing them that most problems have been tackled in one form or another previously, and those solutions can be used to help inform the solution they may envisage.  
                </p>             
                <h4>Process</h4>
                <ul className="bullet-tk">
                  <li>Providing context from the research phase, choose one or multiple How Might We's.</li>
                  <li>With a time box of 25 minutes, ask the participants to research how solutions by other organisations (in any industry) have been designed to solve those similar HMWs.</li>
                  <li>They should provide at least 3 examples or other products, services or features - include images and other notes to help explain how it was solved. </li>
                  <li>Ask them to write down/capture 'The Big Idea' of those examples ready to present to the group later on.</li>
                  <li>1 by 1, present the examples to the rest of the group, with one person assigned to take notes or sketches - preferably on to a whiteboard visible to everyone. For every one 'Big Idea', create a separate note.</li>
                  <li>These notes, sketches, big ideas etc can then all be referenced throughout the ideation process as inspiration.</li>
                </ul>
                <h4>Notes</h4>
                <ul className="bullet-tk">
                  <li>Important to reiterate that no design problem is unique, and generally someone out there has attempted to solve it in the past.</li>
                  <li>Don't have to stay in the same industry, there is a lot of crossover out there.</li>
                  <li>This is a fun thing, relaxing but also educational. It should help even the most creatively challenged to feel comfortable.</li>
                </ul>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.workshopper.com/post/ideation-exercises#toc-6-lightning-demos" className="link-table">Lightning Demos - Workshopper</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-lightning-demos" 
            id="tab-lightning-demos-template"
          />
          <label htmlFor="tab-lightning-demos-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/uXjVM-evNdw=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Lightning Demos Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Grid Section */}
      <main className="case-study">
        <span className="case-tool-title" id="grid">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="grid">
            <span className="tool-title-number">
              <Link to="/design/ideation/warm-up/#grid">Grid</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/design/ideation">Ideation</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/design/ideation/warm-up">Warm Up</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/design/ideation/warm-up/#grid"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-grid" 
            id="tab-grid-overview" 
            defaultChecked
          />
          <label htmlFor="tab-grid-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Warm Up", "14/07/23", gridTemplates)}
              <div className="tool-opening">
                <p>
                  Taking a simple, blank grid, sketch as many options around a particular theme as you can think of within a specific time frame (5 minutes etc). For example, all the uses of a cardboard box, something that floats, or different versions of an Apple.
                </p>
                <p>
                  This activity encourages innovation because it makes the participants think differently whilst keeping them anchored to a core theme.  
                </p>
                <h4>Requirements</h4>
                <ul className="bullet-tk">
                  <li>Paper</li>
                  <li>Pen</li>
                  <li>Clock</li>
                </ul>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.atlassian.com/blog/jira-software/5-fun-sprint-retrospective-ideas-templates" className="link-table">5 fun sprint retrospective ideas with templates - Atlassian</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://beingagile.co.uk/sailboat-retrospective-step-by-step-guide-workshop-video-and-examples-gallery-being-agile/" className="link-table">Retrospective Sailing Game - Being Agile</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-grid" 
            id="tab-grid-template"
          />
          <label htmlFor="tab-grid-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/uXjVMC05IMs=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Grid Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Alternate Uses Section */}
      <main className="case-study">
        <span className="case-tool-title" id="alternate-uses">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="alternate-uses">
            <span className="tool-title-number">
              <Link to="/design/ideation/warm-up/#alternate-uses">Alternate Uses</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/design/ideation">Ideation</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/design/ideation/warm-up">Warm Up</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/design/ideation/warm-up/#alternate-uses"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-alternate-uses" 
            id="tab-alternate-uses-overview" 
            defaultChecked
          />
          <label htmlFor="tab-alternate-uses-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Warm Up", "17/07/23", alternateUsesTemplates)}
              <div className="tool-opening">
                <p>
                  A really nice method of looking at something from a different perspective, expanding how you think and interact with things in a whole new way.
                </p>
                <p>
                  Alternate uses encourages taking a standard object we are all used to using every day, and envisaging how it could be used differently.
                </p>
                <h4>Process</h4>
                <ul className="bullet-tk">
                  <li>Choose an everyday object, a toothbrush, or a cup, or a key, or a pair of shoes.</li>
                  <li>Using sticky notes, encourage the participants to sketch or write down as many alternate uses of the object as possible. The ideas should be a mix of practical, fun and innovative ideas.</li>
                  <li>Give the participants a time boxed period to write down their ideas.</li>
                  <li>Allow the participants to present their ideas as an example to the group as to what can be envisioned when thinking about things differently.</li>
                </ul>
                <h4>Requirements</h4>
                <ul className="bullet-tk">
                  <li>Paper folded in to squares</li>
                  <li>Sticky Notes</li>
                  <li>Pens</li>
                  <li>An everyday object</li>
                </ul>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://lucidspark.com/blog/brainstorming-warm-up-exercises" className="link-table">Brainstorming Warm Up Exercises - Lucid Spark</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-alternate-uses" 
            id="tab-alternate-uses-template"
          />
          <label htmlFor="tab-alternate-uses-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/uXjVM9j5WGs=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Alternate Uses Template"
            ></iframe>
          </div>
        </div>
      </main>
      
      {/* Crazy 8s Section */}
      <main className="case-study">
        <span className="case-tool-title" id="crazy-8s">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="crazy-8s">
            <span className="tool-title-number">
              <Link to="/design/ideation/warm-up/#crazy-8s">Crazy 8s</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/design/ideation">Ideation</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/design/ideation/warm-up">Warm Up</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/design/ideation/warm-up/#crazy-8s"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input
            type="radio"
            name="tabs-crazy-8s"
            id="tab-crazy-8s-overview"
            defaultChecked={true}
          />
          <label htmlFor="tab-crazy-8s-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Warm Up", "17/07/23", crazy8sTemplates, jakeKnappCreator)}
              <div className="tool-opening">
                <p>
                  Whilst seemingly similar to a Grid style warm up, Crazy 8s begins to focus on a problem and encourages the participant to come up with a variety of solutions.
                </p>
                <p>
                  By encouraging broad and divergent thinking by the participants, we can scale our ideas and designs and hopefully come up with not only the best solution, but real innovation that solves the problem within the constraints the team has.
                </p>
                <h4>Process</h4>
                <ul className="bullet-tk">
                  <li>Build context by discussing the problem, scenario or job that a solution is required for.</li>
                  <li>You could then synthesise this in to a 'How Might We' as a way of keeping the participants aligned.</li>
                  <li>The name stems from simply folding a piece of paper in to 8, so do this next.</li>
                  <li>Begin the session, encouraging the participants to sketch ideas in to each space.</li>
                  <li>Use a time limit as a means of forcing rapid creativity amongst the participants.</li>
                  <li>Each participant gets a time period to present their ideas and thinking.</li>
                </ul>
                <h4>Tips</h4>
                <ul className="bullet-tk">
                  <li>There is <b>no</b> pressure to come up with loads of ideas, or any at all.</li>
                  <li>This is a safe space, there are no bad ideas, and nothing is off the table.</li>
                  <li>Use any method (sketching, writing, diagrams, scenarios, interfaces, context etc) you can to try and convey, explain and document your ideas and thoughts.</li>
                  <li>Be humble, sometimes your idea isn't the best, but next time it might be.</li>
                  <li>Chill, relax, be zen etc etc - anything that can help focus the mind on the task and ideas ahead.</li>
                  <li>Practice makes perfect, the more you do this, the more you brain will be trained, the better and fitter you will get.</li>
                </ul>
                <h4>Requirements</h4>
                <ul className="bullet-tk">
                  <li>Paper</li>
                  <li>Pen(s)</li>
                  <li>Clock</li>
                  <li>Multi Colour Sticky Notes</li>
                  <li>Multi Colour Sticky Dots</li>
                </ul>
                <h4>Design Studio</h4>
                <p>
                  Crazy 8s is a particularly helpful warm up, since a similar process will be used during the Design Studio.
                </p>
                <p>
                  If used as part of the Design Studio, you can also employ dot voting as a group to help prioritise the better solutions - this will help with iterating towards a final, holistic design.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.iamnotmypixels.com/how-to-use-crazy-8s-to-generate-design-ideas/" className="link-table">How to run a Crazy Eights exercise to generate design ideas - I Am Not My Pixels</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.youtube.com/watch?v=_LwvsWljr9g" className="link-table">How to generate more UX design ideas with Crazy Eights - femke.design/Youtube</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.switchit.com/blog/design/crazy-concept-ideation-with-crazy-8s.aspx" className="link-table">Crazy Concept Ideation with Crazy 8s - Switch It</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://designsprintkit.withgoogle.com/methodology/phase3-sketch/crazy-8s" className="link-table">Crazy 8's - Design Sprint Kit by Google</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input
            type="radio"
            name="tabs-crazy-8s"
            id="tab-crazy-8s-template"
          />
          <label htmlFor="tab-crazy-8s-template">Template</label>
          <div className="tab tab-tools">
            <iframe
              className="miro_embed"
              src="https://miro.com/app/embed/uXjVMB6th1g=/?&pres=1"
              frameBorder="0"
              scrolling="no"
              allowFullScreen
              title="Crazy 8s Template"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
};

export default WarmUpPage; 